import { useEffect, useRef, useCallback } from "react";

/**
 * Automatically invokes the supplied callback(s) on a regular interval and
 * whenever the browser tab regains visibility.
 *
 * @param {Function|Function[]} callbacks  One or more refetch functions to call.
 *   `undefined` / `null` entries are silently skipped.
 * @param {number|null} [intervalMs=30000]  Polling interval in milliseconds.
 *   - positive number: poll at that cadence + refetch on window focus
 *   - 0: no polling, but still refetch on window focus
 *   - null / undefined: fully disabled (no polling, no focus refetch)
 */
export default function useAutoRefresh(callbacks, intervalMs = 30000) {
  const callbacksRef = useRef(callbacks);
  const isRefreshingRef = useRef(false);

  // Keep the ref in sync without re-running the effect.
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  const invokeAll = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    try {
      const fns = Array.isArray(callbacksRef.current)
        ? callbacksRef.current
        : [callbacksRef.current];
      await Promise.allSettled(
        fns.filter(Boolean).map((fn) => fn()),
      );
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (intervalMs == null) return;

    // Polling — skip ticks when the tab is hidden.
    let timerId;
    if (intervalMs > 0) {
      timerId = setInterval(() => {
        if (document.visibilityState === "visible") {
          invokeAll();
        }
      }, intervalMs);
    }

    // Refetch whenever the user returns to the tab.
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        invokeAll();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (timerId) clearInterval(timerId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [intervalMs, invokeAll]);
}
